import { useMemo } from "react";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { TarefaTipoMap } from "@workflow/types";

import { PeriodoResumoAdminTarefas, TarefaResumoAdminPorDiaDTO } from "@/types/tarefa.type";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { useTarefaAdminResumoPeriodo } from "@/hooks/use-tarefa-admin-resumo-periodo";

import { TAREFA_STATUS_CHART_CONFIG } from "./tarefa-status-visual";
import { TarefaRankedList } from "./TarefaRankedList";

const CONCLUIDAS_CHART_CONFIG = { total: { label: "Concluídas", color: "#10b981" } };

function agruparCriadasPorDia(data: TarefaResumoAdminPorDiaDTO[]) {
  const porDia = new Map<string, { dia: string } & Record<string, string | number>>();
  for (const row of data) {
    const entry = porDia.get(row.dia) ?? { dia: row.dia };
    entry[row.status] = Number(entry[row.status] ?? 0) + row.total;
    porDia.set(row.dia, entry);
  }
  return Array.from(porDia.values())
    .sort((a, b) => (a.dia < b.dia ? -1 : 1))
    .map((e) => ({ ...e, label: format(parseISO(e.dia), "dd/MM", { locale: ptBR }) }));
}

export function TarefaVisaoGeralCard({ dias }: { dias: PeriodoResumoAdminTarefas }) {
  const { resumo, loading } = useTarefaAdminResumoPeriodo(dias);

  const tendenciaCriadas = useMemo(() => agruparCriadasPorDia(resumo.criadasPorDia), [resumo]);
  const tendenciaConcluidas = useMemo(
    () =>
      [...resumo.concluidasPorDia]
        .sort((a, b) => (a.dia < b.dia ? -1 : 1))
        .map((e) => ({ ...e, label: format(parseISO(e.dia), "dd/MM", { locale: ptBR }) })),
    [resumo],
  );
  const porTipo = useMemo(
    () =>
      [...resumo.porTipo]
        .sort((a, b) => b.total - a.total)
        .map((r) => ({ key: r.tipo, label: TarefaTipoMap[r.tipo] ?? r.tipo, value: r.total })),
    [resumo],
  );

  return (
    <div className="rounded-xl border border-border/60 bg-card p-4 shadow-sm">
      <Tabs defaultValue="criadas">
        <div className="mb-3 flex items-center justify-between gap-2">
          <p className="text-sm font-semibold text-foreground">Visão geral</p>
          <TabsList>
            <TabsTrigger value="criadas">Criadas</TabsTrigger>
            <TabsTrigger value="concluidas">Concluídas</TabsTrigger>
            <TabsTrigger value="tipo">Por tipo</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="criadas" className="mt-0">
          {loading ? (
            <Skeleton className="h-64 w-full rounded-xl" />
          ) : resumo.criadasPorDia.length === 0 ? (
            <p className="flex h-64 items-center justify-center text-center text-sm italic text-muted-foreground">
              Nenhuma tarefa criada no período.
            </p>
          ) : (
            <ChartContainer
              config={TAREFA_STATUS_CHART_CONFIG}
              className="!aspect-auto h-64 w-full"
            >
              <BarChart
                data={tendenciaCriadas}
                margin={{ left: 0, right: 8, top: 8, bottom: 0 }}
                barSize={28}
              >
                <defs>
                  {Object.entries(TAREFA_STATUS_CHART_CONFIG).map(([status, cfg]) => (
                    <linearGradient
                      key={status}
                      id={`grad-status-${status}`}
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="0%" stopColor={cfg.color} stopOpacity={1} />
                      <stop offset="100%" stopColor={cfg.color} stopOpacity={0.65} />
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid
                  vertical={false}
                  strokeDasharray="3 3"
                  className="stroke-border/60"
                />
                <XAxis dataKey="label" tickLine={false} axisLine={false} className="text-[11px]" />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  width={28}
                  allowDecimals={false}
                  className="text-[11px]"
                />
                <ChartTooltip
                  cursor={{ className: "fill-muted/40" }}
                  content={<ChartTooltipContent />}
                />
                {Object.keys(TAREFA_STATUS_CHART_CONFIG).map((status) => (
                  <Bar
                    key={status}
                    dataKey={status}
                    stackId="status"
                    fill={`url(#grad-status-${status})`}
                    radius={3}
                  />
                ))}
                <ChartLegend content={<ChartLegendContent />} />
              </BarChart>
            </ChartContainer>
          )}
        </TabsContent>

        <TabsContent value="concluidas" className="mt-0">
          {loading ? (
            <Skeleton className="h-64 w-full rounded-xl" />
          ) : resumo.concluidasPorDia.length === 0 ? (
            <p className="flex h-64 items-center justify-center text-center text-sm italic text-muted-foreground">
              Nenhuma tarefa concluída no período.
            </p>
          ) : (
            <ChartContainer config={CONCLUIDAS_CHART_CONFIG} className="!aspect-auto h-64 w-full">
              <BarChart
                data={tendenciaConcluidas}
                margin={{ left: 0, right: 8, top: 8, bottom: 0 }}
                barSize={28}
              >
                <defs>
                  <linearGradient id="grad-concluidas-total" x1="0" y1="0" x2="0" y2="1">
                    <stop
                      offset="0%"
                      stopColor={CONCLUIDAS_CHART_CONFIG.total.color}
                      stopOpacity={1}
                    />
                    <stop
                      offset="100%"
                      stopColor={CONCLUIDAS_CHART_CONFIG.total.color}
                      stopOpacity={0.65}
                    />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  vertical={false}
                  strokeDasharray="3 3"
                  className="stroke-border/60"
                />
                <XAxis dataKey="label" tickLine={false} axisLine={false} className="text-[11px]" />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  width={28}
                  allowDecimals={false}
                  className="text-[11px]"
                />
                <ChartTooltip
                  cursor={{ className: "fill-muted/40" }}
                  content={<ChartTooltipContent />}
                />
                <Bar dataKey="total" fill="url(#grad-concluidas-total)" radius={3} />
                <ChartLegend content={<ChartLegendContent />} />
              </BarChart>
            </ChartContainer>
          )}
        </TabsContent>

        <TabsContent value="tipo" className="mt-0 h-64 overflow-y-auto pr-1 pt-1">
          {loading ? (
            <Skeleton className="h-56 w-full rounded-xl" />
          ) : (
            <TarefaRankedList rows={porTipo} emptyText="Nenhuma tarefa criada no período." />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
